import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { ConsentScreen } from "./ConsentScreen.tsx";
import { useUsercentricsCmp } from "./useUsercentricsCmp.ts";

type UsercentricsConsentGateProps = {
  /** Usercentrics Settings ID. When empty the gate is disabled and renders children. */
  settingsId: string | undefined;
  logo: ReactNode;
  /** Rendered centered behind the consent banner (e.g. a static splash logo). */
  backdrop?: ReactNode;
  /** Shown while the SDK initialises, before children (which may trigger auth) mount. */
  loadingFallback?: ReactNode;
  children: ReactNode;
};

/**
 * Blocks children with a consent screen until the user decides — wrap around routes that
 * must collect consent before proceeding (e.g. before an ADB2C redirect). On SDK init
 * failure it fails open so a CMP outage never locks users out.
 */
export function UsercentricsConsentGate({
  settingsId,
  logo,
  backdrop,
  loadingFallback = null,
  children,
}: UsercentricsConsentGateProps) {
  if (!settingsId) {
    return children;
  }

  return (
    <ActiveConsentGate
      settingsId={settingsId}
      logo={logo}
      backdrop={backdrop}
      loadingFallback={loadingFallback}
    >
      {children}
    </ActiveConsentGate>
  );
}

type ActiveConsentGateProps = {
  settingsId: string;
  logo: ReactNode;
  backdrop?: ReactNode;
  loadingFallback?: ReactNode;
  children: ReactNode;
};

function ActiveConsentGate({
  settingsId,
  logo,
  backdrop,
  loadingFallback,
  children,
}: ActiveConsentGateProps) {
  const { i18n } = useTranslation();
  const cmp = useUsercentricsCmp(settingsId, i18n.language);
  const [decided, setDecided] = useState(false);

  if (cmp.status === "loading") {
    return loadingFallback;
  }

  if (cmp.status === "ready" && cmp.shouldCollectConsent && !decided) {
    return (
      <ConsentScreen
        cmp={cmp}
        logo={logo}
        backdrop={backdrop}
        onDecision={() => setDecided(true)}
      />
    );
  }

  return children;
}
